import { LegalPageLayout } from "../components/LegalPageLayout";

export function TermsOfService() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      subtitle="Dough Mobile Application"
      lastUpdated="June 2026"
    >
      <section>
        <h2>1. Agreement to Terms</h2>
        <p>
          These Terms of Service ("Terms") constitute a legally binding agreement between you and
          BetterTime Co. ("Company," "we," "us," or "our") concerning your access to and use of the
          Dough mobile application (the "App").
        </p>
        <p>
          By creating an account or using the App, you agree to be bound by these Terms. If you do not
          agree to these Terms, you may not access or use the App.
        </p>
        <p>
          We reserve the right to modify these Terms at any time. We will provide notice of material
          changes by updating the "Last Updated" date and, where appropriate, notifying you through the
          App or via email. Your continued use of the App after any modifications indicates your
          acceptance of the updated Terms.
        </p>
      </section>

      <section>
        <h2>2. Eligibility</h2>
        <p>To use the App, you must meet the following eligibility requirements:</p>

        <h3>Age Requirements</h3>
        <ul>
          <li>You must be at least 13 years old to create an account</li>
          <li>If you are between 13 and 17 years old, you must have permission from a parent or legal guardian to use the App</li>
          <li>By creating an account for a minor, the parent or guardian agrees to these Terms on behalf of the minor and accepts responsibility for the minor's use of the App</li>
        </ul>

        <h3>Account Restrictions</h3>
        <ul>
          <li>You may only create one account</li>
          <li>Each account must be associated with a unique phone number</li>
          <li>You may not create an account using a phone number or email address that has previously been associated with a terminated account</li>
        </ul>

        <h3>Accurate Information</h3>
        <ul>
          <li>You must provide accurate and complete information when creating your account</li>
          <li>You must keep your account information up to date</li>
        </ul>
      </section>

      <section>
        <h2>3. Account Registration and Security</h2>

        <h3>Account Creation</h3>
        <p>
          To use the App, you must create an account by providing your email address, verifying your
          email, and verifying your phone number. You may also sign up using Apple Sign In or Google
          Sign In, but phone verification is still required.
        </p>

        <h3>Account Security</h3>
        <ul>
          <li>You are responsible for maintaining the confidentiality of your account credentials</li>
          <li>You are responsible for all activities that occur under your account</li>
          <li>You must notify us immediately if you suspect any unauthorized use of your account</li>
          <li>We reserve the right to disable any account at any time for any reason, including violation of these Terms</li>
        </ul>

        <h3>One Account Policy</h3>
        <ul>
          <li>Each user may have only one account</li>
          <li>Each phone number may only be associated with one account</li>
          <li>Creating multiple accounts is a violation of these Terms and may result in termination of all associated accounts</li>
        </ul>
      </section>

      <section>
        <h2>4. Description of Services</h2>
        <p>The App provides financial wellness education and tools, including:</p>

        <h3>Budgeting Tool</h3>
        <ul>
          <li>A free budgeting tool that helps you plan and track your finances</li>
          <li>All budgeting data is stored locally on your device only</li>
          <li>We do not access, collect, or store your actual financial information</li>
        </ul>

        <h3>Learning Modules</h3>
        <ul>
          <li>Educational content about financial wellness topics</li>
          <li>Quizzes to test your understanding</li>
          <li>Progress tracking for completed lessons</li>
        </ul>

        <h3>Simulation Game</h3>
        <ul>
          <li>A weekly game featuring financial decision-making scenarios</li>
          <li>Scenarios are generated by artificial intelligence for educational purposes</li>
          <li>Leaderboards showing participant rankings</li>
          <li>Potential rewards for top performers (distributed at our discretion)</li>
        </ul>

        <h3>AI Coach</h3>
        <ul>
          <li>An AI-powered assistant that provides guidance on financial wellness topics</li>
          <li>The AI coach provides general educational information only</li>
          <li>The AI coach does NOT provide personalized financial, investment, tax, or legal advice</li>
        </ul>
      </section>

      <section>
        <h2>5. Important Disclaimers</h2>

        <p className="uppercase-notice">
          NOT FINANCIAL ADVICE: THE APP AND ALL CONTENT, INCLUDING AI COACH RESPONSES, ARE FOR
          EDUCATIONAL AND INFORMATIONAL PURPOSES ONLY. NOTHING IN THE APP CONSTITUTES FINANCIAL,
          INVESTMENT, TAX, OR LEGAL ADVICE.
        </p>
        <ul>
          <li>The AI coach is not a licensed financial advisor, investment advisor, or attorney</li>
          <li>You should consult qualified professionals before making any financial decisions</li>
          <li>We are not responsible for any financial decisions you make based on information from the App</li>
          <li>Past performance in the simulation game does not predict actual financial outcomes</li>
        </ul>

        <h3>No Actual Financial Transactions</h3>
        <ul>
          <li>The App does not connect to your bank accounts</li>
          <li>The App does not process payments or financial transactions</li>
          <li>The budgeting tool is for planning purposes only and does not access real financial data</li>
          <li>The simulation game uses hypothetical scenarios, not real money</li>
        </ul>

        <h3>Accuracy of Information</h3>
        <ul>
          <li>While we strive to provide accurate information, we do not guarantee the accuracy, completeness, or timeliness of any content</li>
          <li>Educational content may become outdated as financial regulations and best practices change</li>
          <li>AI-generated content may contain errors or inaccuracies</li>
        </ul>
      </section>

      <section>
        <h2>6. Simulation Game and Rewards</h2>

        <h3>Participation</h3>
        <ul>
          <li>The simulation game is available to all registered users in good standing</li>
          <li>Participation is voluntary</li>
          <li>Game scenarios and rules may change from week to week</li>
        </ul>

        <h3>Leaderboards</h3>
        <ul>
          <li>Your nickname, avatar, and ranking will be visible to other users on the leaderboard</li>
          <li>Rankings are based on your performance in simulation game scenarios</li>
          <li>We reserve the right to adjust rankings or remove users who violate these Terms</li>
        </ul>

        <h3>Rewards</h3>
        <ul>
          <li>Rewards may be offered to top performers at our sole discretion</li>
          <li>Reward eligibility, amounts, and distribution methods are determined solely by us</li>
          <li>We reserve the right to withhold rewards from users who violate these Terms or engage in fraudulent behavior</li>
          <li>Rewards are distributed through mechanisms separate from the App</li>
          <li>All rewards are subject to applicable taxes, which are the sole responsibility of the recipient</li>
          <li>We reserve the right to modify or discontinue the rewards program at any time</li>
        </ul>

        <h3>Fair Play</h3>
        <ul>
          <li>You must not use bots, scripts, or automated tools to gain an unfair advantage</li>
          <li>You must not exploit bugs or glitches in the game</li>
          <li>You must not collude with other users to manipulate rankings</li>
          <li>Violations may result in disqualification, account termination, and forfeiture of rewards</li>
        </ul>
      </section>

      <section>
        <h2>7. Acceptable Use</h2>
        <p>
          You agree to use the App only for lawful purposes and in accordance with these Terms.
          You agree NOT to:
        </p>

        <h3>Account Abuse</h3>
        <ul>
          <li>Create multiple accounts or share your account with others</li>
          <li>Use someone else's account without permission</li>
          <li>Provide false or misleading information when creating your account</li>
        </ul>

        <h3>Harmful Conduct</h3>
        <ul>
          <li>Harass, abuse, or harm other users</li>
          <li>Impersonate any person or entity</li>
          <li>Engage in any fraudulent activity</li>
        </ul>

        <h3>Technical Abuse</h3>
        <ul>
          <li>Attempt to gain unauthorized access to the App or its systems</li>
          <li>Interfere with or disrupt the App's functionality</li>
          <li>Use the App to distribute malware or other harmful code</li>
          <li>Reverse engineer, decompile, or disassemble any part of the App</li>
          <li>Use automated tools to access or interact with the App</li>
        </ul>

        <h3>Content Violations</h3>
        <ul>
          <li>Use the App to transmit illegal, harmful, or offensive content</li>
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe on the intellectual property rights of others</li>
        </ul>
      </section>

      <section>
        <h2>8. Intellectual Property</h2>

        <h3>Our Property</h3>
        <ul>
          <li>The App, including all content, features, and functionality, is owned by BetterTime Co. and is protected by copyright, trademark, and other intellectual property laws</li>
          <li>Our trademarks, logos, and service marks may not be used without our prior written consent</li>
          <li>Educational content, including lessons and simulation scenarios, is our proprietary content</li>
        </ul>

        <h3>Your Content</h3>
        <ul>
          <li>You retain ownership of any content you create within the App (such as your nickname)</li>
          <li>By using the App, you grant us a non-exclusive, royalty-free license to use, display, and process your content as necessary to provide the services</li>
        </ul>

        <h3>AI-Generated Content</h3>
        <ul>
          <li>Content generated by the AI coach is provided for your personal use</li>
          <li>You may not commercially exploit AI-generated content without our permission</li>
          <li>AI-generated content may be based on publicly available information and does not constitute our endorsement of any particular viewpoint</li>
        </ul>
      </section>

      <section>
        <h2>9. Privacy</h2>
        <p>
          Your privacy is important to us. Our{" "}
          <a href="/privacy">Privacy Policy</a>, available within the App, describes
          how we collect, use, and share your information. By using the App, you agree to our
          Privacy Policy.
        </p>

        <h3>Key Privacy Points</h3>
        <ul>
          <li>We collect email, phone number, and usage data to provide our services</li>
          <li>Budgeting data stays on your device and is not transmitted to us</li>
          <li>AI coach conversations are stored to provide continuity in coaching</li>
          <li>We do not sell your personal information</li>
          <li>You can request deletion of your account and data</li>
        </ul>

        <p>Please review our full Privacy Policy for complete details.</p>
      </section>

      <section>
        <h2>10. Termination</h2>

        <h3>Termination by You</h3>
        <ul>
          <li>You may stop using the App at any time</li>
          <li>You may request deletion of your account through the App or by contacting us</li>
        </ul>

        <h3>Termination by Us</h3>
        <ul>
          <li>We may suspend or terminate your account at any time, with or without cause, with or without notice</li>
          <li>
            Reasons for termination may include, but are not limited to:
            <ul>
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Abusive behavior toward other users or our staff</li>
              <li>Extended period of inactivity</li>
            </ul>
          </li>
        </ul>

        <h3>Effects of Termination</h3>
        <ul>
          <li>Upon termination, your right to use the App ceases immediately</li>
          <li>We may delete your account data in accordance with our data retention policies</li>
          <li>Provisions of these Terms that by their nature should survive termination will survive (including disclaimers, limitations of liability, and intellectual property provisions)</li>
        </ul>

        <h3>Post-Termination Restrictions</h3>
        <ul>
          <li>If your account is terminated for violation of these Terms, you may not create a new account</li>
          <li>Email addresses from terminated accounts are retained to prevent re-registration</li>
        </ul>
      </section>

      <section>
        <h2>11. Disclaimers and Limitation of Liability</h2>

        <p className="uppercase-notice">
          DISCLAIMER OF WARRANTIES: THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
          OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM
          ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT.
        </p>

        <p className="uppercase-notice">
          WE DO NOT WARRANT THAT: THE APP WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE; ANY ERRORS IN
          THE APP WILL BE CORRECTED; THE APP WILL MEET YOUR REQUIREMENTS OR EXPECTATIONS; OR RESULTS
          OBTAINED FROM USING THE APP WILL BE ACCURATE OR RELIABLE.
        </p>

        <p className="uppercase-notice">
          LIMITATION OF LIABILITY: TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL BETTERTIME
          CO., ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS,
          DATA, OR GOODWILL; SERVICE INTERRUPTION OR COMPUTER DAMAGE; OR ANY DAMAGES ARISING FROM YOUR
          USE OF OR INABILITY TO USE THE APP.
        </p>

        <p className="uppercase-notice">
          OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS OR YOUR USE OF THE APP SHALL NOT
          EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRIOR TO THE CLAIM OR
          (B) ONE HUNDRED DOLLARS ($100).
        </p>

        <p>
          Some jurisdictions do not allow the exclusion of certain warranties or limitation of
          liability, so some of the above limitations may not apply to you.
        </p>
      </section>

      <section>
        <h2>12. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless BetterTime Co. and its officers, directors,
          employees, agents, and affiliates from and against any and all claims, liabilities, damages,
          losses, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
        </p>
        <ul>
          <li>Your use of the App</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any rights of another party</li>
          <li>Your violation of any applicable law or regulation</li>
          <li>Any content you submit or transmit through the App</li>
        </ul>
        <p>
          We reserve the right to assume exclusive defense and control of any matter subject to
          indemnification by you, in which case you agree to cooperate with our defense of such claim.
        </p>
      </section>

      <section>
        <h2>13. Dispute Resolution</h2>

        <h3>Governing Law</h3>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of
          Texas, without regard to its conflict of law provisions.
        </p>

        <h3>Informal Resolution</h3>
        <p>
          Before filing any formal legal action, you agree to try to resolve any dispute informally by
          contacting us at <a href="mailto:help@betteryou.ai">help@betteryou.ai</a>. We will try to
          resolve the dispute informally within 60 days.
        </p>

        <h3>Arbitration Agreement</h3>
        <p>
          If we cannot resolve the dispute informally, you and BetterTime Co. agree to resolve any
          disputes through binding arbitration, rather than in court. This means you waive your right
          to a jury trial.
        </p>
        <p>
          Arbitration shall be conducted by a single arbitrator in accordance with the rules of the
          American Arbitration Association. The arbitration shall be held in Austin, Texas, or at
          another mutually agreed location.
        </p>

        <p className="uppercase-notice">
          CLASS ACTION WAIVER: YOU AND BETTERTIME CO. AGREE THAT EACH PARTY MAY BRING CLAIMS AGAINST
          THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN
          ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
        </p>

        <h3>Exceptions</h3>
        <p>
          Either party may bring claims in small claims court if the claims qualify. Either party may
          seek injunctive relief in any court of competent jurisdiction for infringement of intellectual
          property rights.
        </p>
      </section>

      <section>
        <h2>14. General Provisions</h2>

        <h3>Entire Agreement</h3>
        <p>
          These Terms, together with our Privacy Policy, constitute the entire agreement between you
          and BetterTime Co. regarding your use of the App.
        </p>

        <h3>Severability</h3>
        <p>
          If any provision of these Terms is found to be unenforceable, the remaining provisions will
          continue in full force and effect.
        </p>

        <h3>Waiver</h3>
        <p>
          Our failure to enforce any right or provision of these Terms will not be considered a waiver
          of that right or provision.
        </p>

        <h3>Assignment</h3>
        <p>
          You may not assign or transfer your rights under these Terms without our prior written consent.
          We may assign our rights and obligations under these Terms without restriction.
        </p>

        <h3>Notices</h3>
        <p>
          We may provide notices to you through the App, via email to the address associated with your
          account, or by other reasonable means.
        </p>

        <h3>Force Majeure</h3>
        <p>
          We shall not be liable for any failure or delay in performing our obligations due to
          circumstances beyond our reasonable control, including natural disasters, war, terrorism,
          riots, embargoes, acts of civil or military authorities, fire, floods, accidents, or strikes.
        </p>
      </section>

      <section>
        <h2>15. Contact Information</h2>
        <p>
          If you have any questions about these Terms of Service, please contact us:
        </p>
        <p>
          BetterTime Co.<br />
          Email: <a href="mailto:help@betteryou.ai">help@betteryou.ai</a>
        </p>
        <p>
          For legal notices, please send correspondence to the email address above with "Legal Notice"
          in the subject line.
        </p>
      </section>
    </LegalPageLayout>
  );
}
